import React, { useState, useRef, useEffect, useContext } from 'react';
import { PageVisibleContext } from '../common/KeepAliveOutlet';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { assistantAPI, gstAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { IS_LAXMI_HOST, IS_DEMO } from '../../utils/demo';
import './assistant.css';

const LANGS = [['en-IN', 'EN'], ['hi-IN', 'हि'], ['gu-IN', 'ગુ']];
const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
// Spoken answers to a pending preview. Whole-utterance match only, so "yes but make it 30,000" is sent to the assistant instead.
const YES = /^(yes|yeah|yep|confirm|confirmed|ok|okay|do it|go ahead|haan|han|haa|ha|kar do|karo|theek hai|हाँ|हां|हा|कर दो|करो|कन्फर्म|ठीक है|હા|કરો|કરી દો|કન્ફર્મ|બરાબર)( please| ji| जी| જી)?$/i;
const NO = /^(no|nope|cancel|stop|nahi|nahin|na|mat karo|नहीं|नही|ना|रद्द|कैंसल|रद्द करो|ના|નહીં|નથી|કેન્સલ|રદ)( please| ji| जी| જી)?$/i;
// Whole-utterance goodbye: says bye and closes the assistant (a pending preview is cancelled).
const BYE = /^((ok|okay)? ?(thanks|thank you)|thanks|thank you|bye|goodbye|good bye|bye bye|see you|that'?s all|that is all|exit|close|band karo|dhanyavad|shukriya|धन्यवाद|शुक्रिया|बाय|अलविदा|बंद करो|બાય|આભાર|ધન્યવાદ|બંધ કરો)( bye)?( buddy| ji| जी| જી)?$/i;
// Read ₹20,000 as "20,000 rupees" and drop symbols the voice would spell out.
const speakable = (s) => String(s).replace(/₹\s?([\d,.]+)/g, '$1 rupees').replace(/[*_`#✓✗⚠]/g, '').replace(/\s+/g, ' ');
// The voice follows the script the reply is written in; plain Latin text keeps the chosen language (Hinglish stays on it).
const replyLang = (text, fallback) => (/[઀-૿]/.test(text) ? 'gu-IN' : /[ऀ-ॿ]/.test(text) ? 'hi-IN' : fallback);
// Best installed voice for a language: exact locale first, then the natural-sounding network/neural ones
// (Google, Microsoft Online/Natural, Apple Enhanced/Premium) over the robotic default.
const voiceScore = (v, code) => {
  const l = v.lang.replace('_', '-').toLowerCase();
  if (!l.startsWith(code.slice(0, 2).toLowerCase())) return -1;
  return (l === code.toLowerCase() ? 10 : 0) + (/natural|neural|online|google|enhanced|premium/i.test(v.name) ? 5 : 0) + (v.localService ? 0 : 2);
};
const pickVoice = (ss, code) => ss.getVoices().reduce((best, v) => (voiceScore(v, code) > (best ? voiceScore(best, code) : -1) ? v : best), null);

export const AssistantLogo = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M6 7.5A3.5 3.5 0 0 1 9.5 4h13A3.5 3.5 0 0 1 26 7.5v9a3.5 3.5 0 0 1-3.5 3.5H15l-5 4.5V20h-.5A3.5 3.5 0 0 1 6 16.5z" fill="#fff" fillOpacity=".22" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M16 8.2l1.35 3.45 3.45 1.35-3.45 1.35L16 17.8l-1.35-3.45L11.2 13l3.45-1.35z" fill="#fff" />
    <circle cx="23" cy="9" r="1.2" fill="#fff" />
  </svg>
);

function ActionCard({ action, onConfirm, onCancel, onOpenGst, onOpen }) {
  const { preview = {}, status } = action;
  const dl = async () => {
    try {
      const r = await gstAPI.pdf('invoice', action.result.id);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `invoice-${action.result.number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
    } catch { alert('Could not download the PDF. Unlock the GST section and try again.'); }
  };
  return (
    <div className="ai-card">
      <h4><span>{preview.title}</span><span>{{ pending: 'Say "confirm" or "cancel"', running: 'Working…', executed: 'Done', failed: 'Failed', cancelled: 'Cancelled', expired: 'Expired' }[status] || status}</span></h4>
      <table><tbody>{(preview.lines || []).map(([k, v]) => <tr key={k}><td>{k}</td><td>{v}</td></tr>)}</tbody></table>
      {preview.warning && status === 'pending' && <div className="ai-warn">⚠ {preview.warning}</div>}
      {status === 'pending' && (
        <div className="ai-acts">
          <button onClick={onCancel}>Cancel</button>
          <button className="ai-yes" onClick={onConfirm}>Confirm</button>
        </div>
      )}
      {status === 'executed' && (
        <>
          <div className="ai-state ai-state--ok">✓ {action.resultText}</div>
          {action.tool === 'gst_invoice_create' && action.result?.id && <div className="ai-acts"><button onClick={onOpenGst}>View</button><button onClick={dl}>Download PDF</button></div>}
          {action.tool !== 'gst_invoice_create' && action.result?.link && <div className="ai-acts"><button onClick={() => onOpen(action.result.link)}>View</button></div>}
        </>
      )}
      {status === 'failed' && <div className="ai-state ai-state--bad">✗ {action.resultText || action.error || 'It did not go through. Nothing was changed.'}</div>}
      {status === 'cancelled' && <div className="ai-state ai-state--off">Cancelled. Nothing was done.</div>}
    </div>
  );
}

// Name and preferences panel (admin). The name is stored on the server and used everywhere, including in the model's prompt.
function SettingsPanel({ cfg, onSaved, onBack }) {
  const [f, setF] = useState({ name: cfg.name, speak: cfg.speak, language: cfg.language, about: cfg.about || '' });
  const [msg, setMsg] = useState('');
  const save = async () => {
    setMsg('Saving…');
    try { const { data } = await assistantAPI.saveSettings(f); onSaved(data.data); setMsg('Saved.'); } catch (e) { setMsg(e.response?.data?.message || 'Could not save.'); }
  };
  return (
    <div className="ai-settings">
      <label>Name<input value={f.name} maxLength={30} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
      <label>Voice language<select value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}>{LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select></label>
      <label className="ai-check"><input type="checkbox" checked={f.speak} onChange={(e) => setF({ ...f, speak: e.target.checked })} /> Read answers aloud</label>
      <label>About me (how to talk to me, my usual choices)<textarea rows={3} maxLength={600} value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })} /></label>
      {msg && <div className="ai-voice-say">{msg}</div>}
      <div className="ai-acts"><button onClick={onBack}>Back</button><button className="ai-yes" onClick={save}>Save</button></div>
    </div>
  );
}

const PHASE_TEXT ={ idle: 'Tap the circle and speak', listening: 'Listening… speak now', thinking: 'Working on it…', speaking: 'Speaking… tap to interrupt' };

// Voice-only assistant: tap the button, speak, hear the answer. No typing. Everything the conversation needs is in
// refs because the speech callbacks outlive the render that created them.
export default function AssistantFab({ appMode = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // App View keeps the dashboard mounted (hidden, with a frozen pathname) for 30 s after you leave.
  const pageVisible = useContext(PageVisibleContext);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | listening | thinking | speaking
  const [heard, setHeard] = useState('');
  const [reply, setReply] = useState('');
  const [action, setActionState] = useState(null);
  const [lang, setLang] = useState(0);
  const [cfg, setCfg] = useState({ name: 'Buddy AI', speak: true, language: 'en-IN' });
  const [showSettings, setShowSettings] = useState(false);
  const langRef = useRef(0);
  const speakRef = useRef(true);
  const chatIdRef = useRef(null);
  const actionRef = useRef(null);
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const speakSeq = useRef(0);

  const setAction = (a) => { actionRef.current = a; setActionState(a); };
  const patchAction = (patch) => setAction({ ...actionRef.current, ...patch });

  const stopSpeech = () => {
    speakSeq.current += 1; // drops any server audio still loading
    try { audioRef.current?.pause(); } catch { /* ignore */ }
    audioRef.current = null;
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
  };
  const stopAll = () => {
    const rec = recRef.current; recRef.current = null;
    try { rec?.abort(); } catch { /* ignore */ }
    stopSpeech();
  };
  useEffect(() => stopAll, []);
  // Chrome fills the voice list late; touching it early means the first answer finds a voice instead of silence.
  useEffect(() => { try { window.speechSynthesis?.getVoices(); } catch { /* ignore */ } }, []);

  const applyCfg = (c) => {
    setCfg(c); speakRef.current = c.speak !== false;
    const i = Math.max(0, LANGS.findIndex(([code]) => code === c.language));
    langRef.current = i; setLang(i);
  };
  const role = user?.role;
  useEffect(() => {
    if (!['admin', 'staff', 'co-admin'].includes(role) || !IS_LAXMI_HOST || IS_DEMO) return;
    assistantAPI.settings().then(({ data }) => applyCfg(data.data)).catch(() => { /* keep the default name */ });
  }, [role]);

  // Speaks `text`, then runs `then`. If this browser has no voice for the chosen language, the text on screen is the reply.
  const speak = (text, then) => {
    setReply(text);
    if (!speakRef.current) { setPhase('idle'); if (then) then(); return; }
    // Neural voice from the server first; any failure drops to the browser's own voice below.
    const id = ++speakSeq.current;
    setPhase('speaking');
    assistantAPI.tts(speakable(text), replyLang(text, LANGS[langRef.current][0])).then(({ data }) => {
      if (id !== speakSeq.current) return; // interrupted while the audio was loading
      const url = URL.createObjectURL(data);
      const a = new Audio(url); audioRef.current = a;
      const fin = () => { URL.revokeObjectURL(url); if (audioRef.current === a) audioRef.current = null; if (id === speakSeq.current) { if (then) then(); else setPhase('idle'); } };
      a.onended = fin; a.onerror = fin;
      a.play().catch(() => { URL.revokeObjectURL(url); audioRef.current = null; if (id === speakSeq.current) browserSpeak(text, then); });
    }).catch(() => { if (id === speakSeq.current) browserSpeak(text, then); });
  };

  const browserSpeak = (text, then) => {
    const ss = typeof window !== 'undefined' ? window.speechSynthesis : null;
    const code = LANGS[langRef.current][0];
    const voice = ss && pickVoice(ss, code);
    if (!ss || !voice) { setPhase('idle'); if (then) then(); return; }
    ss.cancel();
    const u = new SpeechSynthesisUtterance(speakable(text));
    u.lang = voice.lang; u.voice = voice; u.rate = 1.05; // ponytail: fixed rate, add a settings slider if the owner wants it
    let done = false;
    const fin = () => { if (done) return; done = true; if (then) then(); else setPhase('idle'); };
    u.onend = fin; u.onerror = fin;
    setPhase('speaking'); ss.speak(u);
  };

  const listen = () => {
    if (!SR || recRef.current) return;
    stopSpeech();
    const rec = new SR();
    rec.lang = LANGS[langRef.current][0]; rec.interimResults = true; rec.continuous = false;
    let text = '';
    rec.onresult = (ev) => { text = Array.from(ev.results).map((r) => r[0].transcript).join(''); setHeard(text); };
    rec.onend = () => {
      if (recRef.current !== rec) return; // aborted on purpose
      recRef.current = null;
      const t = text.trim();
      if (t) handle(t); else setPhase('idle'); // heard nothing: wait for a tap rather than listening forever
    };
    rec.onerror = () => { text = ''; };
    recRef.current = rec; setHeard(''); setPhase('listening');
    try { rec.start(); } catch { recRef.current = null; setPhase('idle'); }
  };

  const doConfirm = async (a) => {
    setPhase('thinking'); patchAction({ status: 'running' });
    try {
      const { data } = await assistantAPI.confirm(a._id);
      patchAction({ status: data.state, resultText: data.message, result: data.result });
      speak(data.message);
    } catch (e) {
      const m = e.response?.data?.message || 'No reply from the server, so I cannot say it worked. Check the GST list before trying again.';
      patchAction({ status: 'failed', resultText: m });
      speak(m);
    }
  };
  const doCancel = async (a) => {
    setPhase('thinking');
    try { await assistantAPI.cancel(a._id); } catch { /* the ticket expires on its own */ }
    patchAction({ status: 'cancelled' });
    speak('Cancelled. Nothing was done.');
  };

  const handle = async (t) => {
    const norm = t.toLowerCase().replace(/[.,!?।]/g, '').trim();
    const a = actionRef.current;
    if (BYE.test(norm)) return speak('Bye!', cancelAll);
    if (a && a.status === 'pending') {
      if (YES.test(norm)) return doConfirm(a);
      if (NO.test(norm)) return doCancel(a);
    }
    setPhase('thinking'); setReply('');
    try {
      const { data } = await assistantAPI.chat(t, chatIdRef.current);
      const d = data.data;
      chatIdRef.current = d.chatId;
      if (d.code === 'GST_LOCKED') { setAction(null); return speak(`${d.say} Open the GST section and unlock it first.`); }
      if (d.action) {
        const act = { status: 'pending', ...d.action, _id: d.actionId };
        setAction(act);
        const lines = act.preview?.lines || [];
        const key = [lines[0], lines[2], lines[lines.length - 1]].filter(Boolean).map(([k, v]) => `${k}: ${v}`).join('. ');
        return speak(`${d.say} ${key}. Say confirm or cancel.`, listen);
      }
      setAction(null);
      speak(d.say, listen);
    } catch (e) {
      speak(e.response?.data?.message || 'Could not reach the assistant. Try again.');
    }
  };

  const tapOrb = () => {
    if (phase === 'listening') { try { recRef.current?.stop(); } catch { /* ignore */ } return; } // stop = send what was heard
    if (phase === 'speaking') { stopAll(); setPhase('idle'); return listen(); }
    if (phase === 'idle') listen();
  };
  const nextLang = () => { const n = (lang + 1) % LANGS.length; langRef.current = n; setLang(n); };
  // Browsers only allow the microphone to start from a tap, so opening and listening happen in the same tap.
  const openAndListen = () => { setOpen(true); listen(); };
  // Cancel = stop everything and close; a preview that was never confirmed is cancelled on the server too.
  const cancelAll = () => {
    const a = actionRef.current;
    if (a && a.status === 'pending') assistantAPI.cancel(a._id).catch(() => { /* the ticket expires on its own */ });
    close();
  };
  const close = () => { stopAll(); setShowSettings(false); setOpen(false); setPhase('idle'); setHeard(''); setReply(''); setAction(null); chatIdRef.current = null; };
  // The layout stays mounted across pages, so stop and reset when the user leaves the dashboard.
  useEffect(() => { if (pathname !== '/dashboard' || !pageVisible) close(); }, [pathname, pageVisible]);
  // The assistant belongs to Laxmi's own CRM: its brain runs on this server. Staff/admin only.
  // Dashboard only: leaving the page also stops any listening or speaking (see the unmount cleanup above).
  if (!user || !['admin', 'staff', 'co-admin'].includes(user.role) || !IS_LAXMI_HOST || IS_DEMO || pathname !== '/dashboard' || !pageVisible) return null;

  const goGst = () => { close(); navigate('/gst-billing'); };

  // Portal to <body>: a transformed ancestor (pull-to-refresh, page transitions) would otherwise break position: fixed.
  return ReactDOM.createPortal(
    <>
      {!open && (
        <button className={`ai-fab${appMode ? ' ai-fab--app' : ''}`} onClick={openAndListen} aria-label={`Talk to ${cfg.name}`} title={`Talk to ${cfg.name}`}>
          <AssistantLogo size={appMode ? 24 : 26} />
        </button>
      )}
      {open && (
        <div className="ai-scrim" onClick={cancelAll}>
          <div className="ai-voice" role="dialog" aria-label={cfg.name} onClick={(e) => e.stopPropagation()}>
            <div className="ai-voice-title">{cfg.name}</div>
            {showSettings ? (
              <SettingsPanel cfg={cfg} onSaved={applyCfg} onBack={() => setShowSettings(false)} />
            ) : !SR ? (
              <div className="ai-voice-say">This browser cannot listen. Open the CRM in Chrome (Android / desktop) to talk to the assistant.</div>
            ) : (
              <>
                <button className={`ai-orb ai-orb--${phase}`} onClick={tapOrb} aria-label={PHASE_TEXT[phase]}>
                  <span className="ai-wave"><i /><i /><i /><i /><i /></span>
                </button>
                <div className="ai-voice-status">{PHASE_TEXT[phase]}</div>
                {heard && phase !== 'speaking' && <div className="ai-voice-heard">{heard}</div>}
                {reply && <div className="ai-voice-say">{reply}</div>}
                {action && <ActionCard action={action} onConfirm={() => doConfirm(action)} onCancel={() => doCancel(action)} onOpenGst={goGst} onOpen={(p) => { close(); navigate(p); }} />}
              </>
            )}
            <div className="ai-voice-bar">
              {SR && <button className="ai-lang" onClick={nextLang} aria-label="Voice language">{LANGS[lang][1]}</button>}
              {role === 'admin' && !showSettings && <button className="ai-lang" onClick={() => { stopAll(); setPhase('idle'); setShowSettings(true); }} aria-label={`${cfg.name} settings`} title="Settings">⚙</button>}
              <button className="ai-cancel" onClick={cancelAll}>{phase === 'idle' && reply ? 'Done' : 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
